// The Business entitlement for one organization. Business is its own plan
// slot (`plan_key='business'`, state trialing/active); cloud and the AI
// platform are separate products and never imply it. The gate fails closed
// on error and never falls back to a personal organization.
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getOrganizationEntitlement } from "@/lib/api";
import { useSession } from "@/lib/session";

interface BusinessEntitlementValue {
  /** True until the first entitlement read resolves. */
  loading: boolean;
  /** Active Business entitlement; false while loading or after an error. */
  business: boolean;
  /** Set when the entitlement could not be loaded; the gate stays closed. */
  error: string | null;
  refresh: () => Promise<void>;
}

const BusinessEntitlementContext =
  createContext<BusinessEntitlementValue | null>(null);

export function BusinessEntitlementProvider({
  children,
  organizationId: requestedOrganizationId,
}: {
  children: ReactNode;
  organizationId?: string;
}) {
  const { state } = useSession();
  const organizationId =
    state.status === "authenticated" ? requestedOrganizationId ?? null : null;
  const identity = state.status === "authenticated" ? state.user.id : "anonymous";
  return <EntitlementScope key={`${identity}:${organizationId}`} organizationId={organizationId}>{children}</EntitlementScope>;
}

function EntitlementScope({ children, organizationId }: { children: ReactNode; organizationId: string | null }) {
  const generation = useRef(0);
  const [value, setValue] = useState<
    Omit<BusinessEntitlementValue, "refresh">
  >({ loading: true, business: false, error: null });

  const refresh = useCallback(async () => {
    const requestGeneration = ++generation.current;
    if (!organizationId) {
      setValue({ loading: false, business: false, error: null });
      return;
    }
    try {
      const entitlement = await getOrganizationEntitlement(organizationId);
      if (requestGeneration !== generation.current) return;
      setValue({ loading: false, business: entitlement.business, error: null });
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      // Fail closed: a failed read is never treated as entitled.
      setValue({
        loading: false,
        business: false,
        error: cause instanceof Error ? cause.message : String(cause),
      });
    }
  }, [organizationId]);

  useEffect(() => {
    void refresh();
    // Background reads do not unmount a dirty editor on every successful
    // refresh. Errors/revocations close access; the API rechecks each request.
    const onVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(onVisible, 60_000);
    return () => {
      ++generation.current;
      window.clearInterval(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  return (
    <BusinessEntitlementContext.Provider value={{ ...value, refresh }}>
      {children}
    </BusinessEntitlementContext.Provider>
  );
}

export function useBusinessEntitlement(): BusinessEntitlementValue {
  const value = useContext(BusinessEntitlementContext);
  if (!value) {
    throw new Error(
      "useBusinessEntitlement() must be used inside BusinessEntitlementProvider",
    );
  }
  return value;
}
